import { memo } from 'react';
import { uniqueId } from 'lodash-es';
import { AText, ATextBreedProps, Box } from '@alf/uikit';
import { useI18n } from '@namespace/i18n';
import { Icon } from '@namespace/icons2';
import {
  COLUMN_TYPES,
  LINE_VIEW,
  TEXT_BREED_BY_COLUMN_TYPES
} from '../../../../constants';
import { LOCALISED_RESULT_TYPE_TAGS } from '../../constants';
import {
  isScoreObject,
  isScoreString,
  ScoreDataType,
  ScoreType
} from '../../types';
import Penalty from './components/Penalty';
import RoundIcon from './components/RoundIcon';
import ScoreCell from './components/ScoreCell';

import styles from './index.module.css';

type LineViewType = (typeof LINE_VIEW)[keyof typeof LINE_VIEW];

const getStyleByLineView = (lineView: LineViewType) => {
  return (styles as Record<LineViewType, string>)[lineView] || '';
};

type ScoreProps = {
  type: string;
  scores: ScoreType[];
  lineView: LineViewType;
  isShortVersion: boolean;
};

const Score = memo(
  ({ type, scores = [], lineView, isShortVersion }: ScoreProps) => {
    const lineViewClass = getStyleByLineView(lineView);
    const BASE_SCORE_CELL_CLASSES = `${lineViewClass} ${!isShortVersion ? styles.fullVersionCell : ''}`;

    const renderRoundIconScores = () => {
      if (!scores.length && !isShortVersion) {
        return <div className={styles.roundTypeCellEmpty} />;
      }

      return scores.map((score) =>
        isScoreString(score) ? (
          <RoundIcon
            key={uniqueId('scoreCell_')}
            score={score}
            className={`${styles.penaltyCell} ${BASE_SCORE_CELL_CLASSES}`}
          />
        ) : null
      );
    };

    const renderPenaltyScores = () =>
      scores.map((score) =>
        isScoreObject(score) ? (
          <Penalty
            key={uniqueId('scoreCell_')}
            scopeId={score.scopeId}
            className={`${styles.penaltyCell} ${BASE_SCORE_CELL_CLASSES}`}
          />
        ) : null
      );

    const renderPointsScores = () =>
      scores.map((score) =>
        isScoreString(score) ? (
          <ScoreCell
            key={uniqueId('scoreCell_')}
            score={score}
            className={`${styles.scoreCell} ${styles.separated} ${BASE_SCORE_CELL_CLASSES}`}
          />
        ) : null
      );

    const renderDefaultScores = () =>
      scores.map((score) =>
        isScoreString(score) ? (
          <ScoreCell
            key={uniqueId('scoreCell_')}
            score={score}
            breed={type === COLUMN_TYPES.TOTAL ? 'B' : 'R'}
            className={`${styles.scoreCell} ${BASE_SCORE_CELL_CLASSES}`}
          />
        ) : null
      );

    const renderScoreColumnsByType = () => {
      switch (type) {
        case COLUMN_TYPES.ROUND_ICON:
          return renderRoundIconScores();
        case COLUMN_TYPES.PENALTY:
        case COLUMN_TYPES.BULLETS:
          return renderPenaltyScores();
        case COLUMN_TYPES.POINTS:
          return renderPointsScores();
        default:
          return renderDefaultScores();
      }
    };

    return <>{renderScoreColumnsByType()}</>;
  }
);

type ColumnProps = ScoreDataType;

const Column = ({
  resultTypeId,
  type,
  tag,
  tagIconColor,
  tagIcon,
  scores,
  isShortVersion,
  lineView
}: ColumnProps) => {
  const { t } = useI18n();
  const breed = TEXT_BREED_BY_COLUMN_TYPES[type] as ATextBreedProps;

  return scores ? (
    <Box direction="column">
      {!isShortVersion && (
        <Box align="center" justify="center" className={styles.title}>
          {tagIcon ? (
            <Icon
              name={`icons/event/${tagIcon}`}
              size="xs"
              color={tagIconColor}
            />
          ) : (
            <AText color="line_txt_3" breed={breed} size="m">
              {LOCALISED_RESULT_TYPE_TAGS.includes(resultTypeId) ? t(tag) : tag}
            </AText>
          )}
        </Box>
      )}
      <Box direction="column">
        <Score
          type={type}
          scores={scores}
          lineView={lineView}
          isShortVersion={isShortVersion}
        />
      </Box>
    </Box>
  ) : null;
};

export default memo(Column);
